import { estimateSurfFaceHeight, type BreakType } from "@/lib/marine";
import { getCanonicalUrl } from "@/lib/seo";
import {
  publicSpotId,
  type PublicForecastResponse,
  type TideTrendLabel,
} from "@/lib/publicForecast";
import {
  aboutReport,
  answerConfigForPath,
  buildFaqJsonLd,
  compass8,
  factsFromPublishedForecast,
  factsFromReadings,
  formatConditionsSentence,
  formatLocalTimestamp,
  rateAnswer,
  type AnswerFacts,
  type FaqEntry,
} from "@/lib/seoAnswer";
import { getNextTides, tideAtTime, tideDataFromApi, type TideData } from "@/lib/tides";
import { convertHeight } from "@/lib/units";
import type { Camera, RegionConfig, SurfBreak } from "@/lib/regions";
import fallbackRegions from "@/lib/regionsSnapshot.json";

export const FLAGLER_TIDE_STATION_ID = "noaa/8721147";
export const FLAGLER_REGION_ID = "Northeast Florida";
export const FLAGLER_PIER_ID = "flagler-beach-pier";
export const FLAGLER_SURF_CAM_ID = "flagler-beach-flaglersurf-cam";
export const FLAGLER_REPORT_PATH = "/flagler-beach-surf-report";

export type FlaglerTopic = "report" | "cam" | "tide" | "water";

export interface FlaglerLanding {
  topic: FlaglerTopic;
  path: string;
  title: string;
  description: string;
  h1: string;
  intro: string;
  linkLabel: string;
  placeName: string;
  lat: number;
  lon: number;
  breakId: string;
  priority: "0.95";
}

export interface FlaglerTideExtreme {
  feet: number;
  meters: number;
  timestampLocal: string;
}

export interface FlaglerTideReading {
  feet: number;
  meters: number;
  trend: "rising" | "falling" | "steady" | TideTrendLabel;
  stationId: string;
  stationName: string;
  isPonceInlet: boolean;
  distanceKm: number | null;
  timestampIso: string;
  timestampLocal: string;
  nextHigh: FlaglerTideExtreme | null;
  nextLow: FlaglerTideExtreme | null;
}

export interface FlaglerWaterReading {
  fahrenheit: number;
  celsius: number;
  timestampIso: string;
  timestampLocal: string;
}

export interface FlaglerWindReading {
  mph: number;
  direction: string;
  timestampIso: string;
  timestampLocal: string;
}

export interface FlaglerReadings {
  surf: AnswerFacts | null;
  surfObservedAt: string | null;
  tide: FlaglerTideReading | null;
  water: FlaglerWaterReading | null;
  wind: FlaglerWindReading | null;
}

export interface FlaglerLink {
  href: string;
  label: string;
  external?: boolean;
}

export interface FlaglerSection {
  id: string;
  heading: string;
  paragraphs: string[];
  links: FlaglerLink[];
}

/** Queries for the daily rank check. Paths are the landing pages above. */
export const FLAGLER_RANK_QUERIES = [
  { query: "flagler beach surf cam", path: "/flagler-beach-surf-cam" },
  { query: "flagler beach surf report", path: FLAGLER_REPORT_PATH },
  { query: "flagler beach tide", path: "/flagler-beach-tide" },
  { query: "flagler beach water temperature", path: "/flagler-beach-water-temperature" },
  { query: "how is the surf", path: "/" },
] as const;

const PALM_COAST_ANSWER =
  "No. Palm Coast is just inland of Flagler Beach and does not have its own surf report or cam on howisthe.surf. Use the Flagler Beach pages for the surf report, surf cam, tide, and water temperature.";

const regions = fallbackRegions as unknown as Record<string, RegionConfig>;

function flaglerRegion(): RegionConfig {
  const region = regions[FLAGLER_REGION_ID];
  if (!region) throw new Error("Northeast Florida is missing from the region snapshot");
  return region;
}

export function flaglerBreak(breakId: string): SurfBreak {
  const found = flaglerRegion().breaks.find((entry) => entry.id === breakId);
  if (!found) throw new Error(`Missing Flagler break ${breakId}`);
  return found;
}

const pierBreak = flaglerBreak(FLAGLER_PIER_ID);
const camBreak = flaglerBreak(FLAGLER_SURF_CAM_ID);

export function flaglerTimeZone(): string {
  return flaglerRegion().timezone || "America/New_York";
}

export function flaglerConditionsPoint(): {
  lat: number;
  lon: number;
  breakType: BreakType;
  shoreNormal: number | null;
  timeZone: string;
} {
  return {
    lat: pierBreak.lat,
    lon: pierBreak.lon,
    breakType: pierBreak.breakType ?? "beach",
    shoreNormal: pierBreak.shoreNormal ?? null,
    timeZone: flaglerTimeZone(),
  };
}

export function flaglerSurfCamUrl(): string {
  return camBreak.cameras[0]?.url ?? "";
}

export function flaglerPierCamera(): Camera | null {
  const camera = pierBreak.cameras[0];
  if (!camera?.url || camera.kind === "link") return null;
  return {
    id: pierBreak.id,
    name: pierBreak.name,
    url: camera.url,
    kind: "stream",
    source: camera.source,
  };
}

export function flaglerSurfCamera(): Camera | null {
  const camera = camBreak.cameras[0];
  if (!camera?.url) return null;
  return {
    id: camBreak.id,
    name: camBreak.name,
    url: camera.url,
    kind: camera.kind ?? "link",
    source: camera.source,
  };
}

function landing(
  topic: FlaglerTopic,
  path: string,
  title: string,
  description: string,
  h1: string,
  intro: string,
  placeName: string,
  spot: SurfBreak,
): FlaglerLanding {
  return {
    topic,
    path,
    title,
    description,
    h1,
    intro,
    linkLabel: h1,
    placeName,
    lat: spot.lat,
    lon: spot.lon,
    breakId: spot.id,
    priority: "0.95",
  };
}

export const flaglerLandingPages: FlaglerLanding[] = [
  landing(
    "report",
    FLAGLER_REPORT_PATH,
    "Flagler Beach Surf Report – Waves, Tide & Water Temp | How Is the Surf",
    "Flagler Beach surf report with live swell, wind, tide at Ponce de Leon Inlet (NOAA 8721147), water temperature, and the FlaglerSurf cam.",
    "Flagler Beach surf report",
    "Flagler Beach is the live-cam check on this exposed Northeast Florida coast. Short-period wind swell, the tide at Ponce de Leon Inlet, and local wind shifts change the session quickly. Palm Coast sits just inland and does not have its own surf page here.",
    "Flagler Beach Pier",
    pierBreak,
  ),
  landing(
    "cam",
    "/flagler-beach-surf-cam",
    "Flagler Beach Surf Cam – Live FlaglerSurf Camera | How Is the Surf",
    "Flagler Beach surf cam: the FlaglerSurf camera and the Flagler Beach Pier stream, with swell, tide, and water temperature when those readings are in.",
    "Flagler Beach surf cam",
    "The FlaglerSurf cam looks at the beach from town and opens on FlaglerSurf.com. This page also plays the Flagler Beach Pier stream. Swell, wind, tide, and water temperature are the Flagler Beach readings, not a second forecast. Palm Coast does not have a separate cam here.",
    "Flagler Beach (FlaglerSurf Cam)",
    camBreak,
  ),
  landing(
    "tide",
    "/flagler-beach-tide",
    "Flagler Beach Tide – Ponce de Leon Inlet (8721147) | How Is the Surf",
    "Flagler Beach tide from Ponce de Leon Inlet, NOAA station 8721147, with the surf report, water temperature, and surf cam.",
    "Flagler Beach tide",
    "Flagler Beach tide on this page is the prediction for Ponce de Leon Inlet, NOAA station 8721147. That is the gauge used for Flagler Pier and the FlaglerSurf cam. The inlet is south of town, so use it as the local tide reference rather than a sensor on the pier. Palm Coast shares this coast's tide and does not have a separate page.",
    "Flagler Beach",
    pierBreak,
  ),
  landing(
    "water",
    "/flagler-beach-water-temperature",
    "Flagler Beach Water Temperature – Today's Ocean Reading | How Is the Surf",
    "Flagler Beach water temperature in Fahrenheit and Celsius when a sea-surface reading is available, plus tide, swell, and the surf cam.",
    "Flagler Beach water temperature",
    "Flagler Beach water temperature is the sea-surface reading for this break when the marine data includes one. It is shown in Fahrenheit, with Celsius beside it. If the reading is missing, the number is left out. Palm Coast does not have a separate water-temperature page.",
    "Flagler Beach",
    pierBreak,
  ),
];

const byPath = new Map(flaglerLandingPages.map((page) => [page.path, page]));

export function getFlaglerLanding(pathname: string): FlaglerLanding | null {
  const path = pathname.split(/[?#]/)[0];
  const normalized = path.length > 1 ? path.replace(/\/+$/, "") : path;
  return byPath.get(normalized.startsWith("/") ? normalized : `/${normalized}`) ?? null;
}

export function emptyFlaglerReadings(): FlaglerReadings {
  return { surf: null, surfObservedAt: null, tide: null, water: null, wind: null };
}

function finite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

function oneDecimal(value: number): string {
  return round1(value).toFixed(1);
}

function stamp(
  iso: string,
  timeZone: string,
): { timestampIso: string; timestampLocal: string } | null {
  const timestampLocal = formatLocalTimestamp(iso, timeZone);
  if (!timestampLocal || Number.isNaN(Date.parse(iso))) return null;
  return { timestampIso: new Date(iso).toISOString(), timestampLocal };
}

export function tideReadingFromData(
  data: TideData | null | undefined,
  nowIso: string,
  timeZone: string,
): FlaglerTideReading | null {
  if (!data || data.hourly.length < 2) return null;
  const now = Date.parse(nowIso);
  if (Number.isNaN(now)) return null;
  const start = data.hourly[0].time.getTime();
  const end = data.hourly[data.hourly.length - 1].time.getTime();
  if (now < start || now > end) return null;
  const sample = tideAtTime(data, nowIso);
  if (!sample || !finite(sample.height)) return null;
  const timed = stamp(nowIso, timeZone);
  if (!timed) return null;
  const stationId = data.stationId ?? "";
  const rawName = data.stationName?.trim() ?? "";
  const stationName = rawName && rawName !== "Unknown" ? rawName : stationId;
  if (!stationName) return null;
  const upcoming = getNextTides(data.predictions, 8);
  const extreme = (type: "H" | "L"): FlaglerTideExtreme | null => {
    const next = upcoming.find((entry) => entry.type === type && finite(entry.height));
    if (!next) return null;
    const local = formatLocalTimestamp(next.time.toISOString(), timeZone);
    if (!local) return null;
    return {
      feet: round1(convertHeight(next.height, "imperial")),
      meters: round1(next.height),
      timestampLocal: local,
    };
  };
  return {
    feet: round1(convertHeight(sample.height, "imperial")),
    meters: round1(sample.height),
    trend: sample.trend,
    stationId,
    stationName,
    isPonceInlet: stationId === FLAGLER_TIDE_STATION_ID,
    distanceKm: finite(data.stationDistanceKm) ? data.stationDistanceKm : null,
    ...timed,
    nextHigh: extreme("H"),
    nextLow: extreme("L"),
  };
}

function waterReading(
  celsius: number | null | undefined,
  observedAt: string | null,
  timeZone: string,
): FlaglerWaterReading | null {
  if (!finite(celsius) || !observedAt) return null;
  const timed = stamp(observedAt, timeZone);
  if (!timed) return null;
  return {
    fahrenheit: Math.round(celsius * 1.8 + 32),
    celsius: Math.round(celsius),
    ...timed,
  };
}

function windReading(
  mph: number,
  direction: string,
  observedAt: string,
  timeZone: string,
): FlaglerWindReading | null {
  const timed = stamp(observedAt, timeZone);
  if (!timed || !finite(mph) || !direction) return null;
  return { mph, direction, ...timed };
}

function marineObservedAt(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const record = body as {
    current?: { time?: unknown };
    utc_offset_seconds?: unknown;
  };
  const local = record.current?.time;
  const offset = record.utc_offset_seconds;
  if (typeof local !== "string" || typeof offset !== "number") return null;
  const asUtc = Date.parse(`${local}Z`);
  if (Number.isNaN(asUtc)) return null;
  return new Date(asUtc - offset * 1000).toISOString();
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

export function readingsFromLivePayloads(input: {
  marine: unknown;
  wind: unknown;
  tide: unknown;
  nowIso?: string;
}): FlaglerReadings {
  const point = flaglerConditionsPoint();
  const marine = record(input.marine);
  const current = record(marine.current);
  const windNow = record(record(input.wind).current);
  const observedAt = marineObservedAt(input.marine);
  const timeZone =
    typeof marine.timezone === "string" && marine.timezone
      ? marine.timezone
      : point.timeZone;
  const offshoreM = current.wave_height;
  const periodS = current.wave_period;
  let surf: AnswerFacts | null = null;
  if (
    observedAt &&
    finite(offshoreM) &&
    finite(periodS) &&
    finite(windNow.wind_speed_10m) &&
    finite(windNow.wind_direction_10m)
  ) {
    const swellHeightM = finite(current.swell_wave_height)
      ? current.swell_wave_height
      : offshoreM;
    const swellPeriodS = finite(current.swell_wave_period)
      ? current.swell_wave_period
      : periodS;
    const swellDirectionDeg = finite(current.swell_wave_direction)
      ? current.swell_wave_direction
      : finite(current.wave_direction)
        ? current.wave_direction
        : null;
    const faceHeightM = estimateSurfFaceHeight(offshoreM, periodS, point.breakType);
    surf = factsFromReadings({
      rating: rateAnswer({
        faceHeightM,
        periodS: swellPeriodS,
        windSpeedKmh: windNow.wind_speed_10m,
        windGustKmh: finite(windNow.wind_gusts_10m) ? windNow.wind_gusts_10m : null,
        windDirectionDeg: windNow.wind_direction_10m,
        swellDirectionDeg,
        shoreNormal: point.shoreNormal,
      }),
      faceHeightM,
      swellHeightM,
      swellPeriodS,
      swellDirectionDeg,
      windSpeedKmh: windNow.wind_speed_10m,
      windDirectionDeg: windNow.wind_direction_10m,
      updatedAtIso: observedAt,
      timeZone,
    });
  }
  const tide = tideReadingFromData(
    tideDataFromApi(input.tide as Parameters<typeof tideDataFromApi>[0]),
    input.nowIso ?? new Date().toISOString(),
    timeZone,
  );
  const water = waterReading(
    finite(current.sea_surface_temperature) ? current.sea_surface_temperature : null,
    observedAt,
    timeZone,
  );
  const wind =
    surf && observedAt
      ? windReading(surf.windMph, surf.windDir, observedAt, timeZone)
      : windFromPayload(input.wind, timeZone);
  return {
    surf,
    surfObservedAt: surf ? observedAt : null,
    tide,
    water,
    wind,
  };
}

function windFromPayload(payload: unknown, timeZone: string): FlaglerWindReading | null {
  const observedAt = marineObservedAt(payload);
  const current = record(record(payload).current);
  const direction = compass8(
    finite(current.wind_direction_10m) ? current.wind_direction_10m : null,
  );
  if (!observedAt || !finite(current.wind_speed_10m) || !direction) return null;
  return windReading(current.wind_speed_10m * 0.621371, direction, observedAt, timeZone);
}

function waterFromFahrenheit(
  fahrenheit: number | null | undefined,
  observedAt: string,
  timeZone: string,
): FlaglerWaterReading | null {
  if (!finite(fahrenheit)) return null;
  const timed = stamp(observedAt, timeZone);
  if (!timed) return null;
  return {
    fahrenheit: Math.round(fahrenheit),
    celsius: Math.round((fahrenheit - 32) / 1.8),
    ...timed,
  };
}

function tideFromPublished(
  feet: number | null | undefined,
  trend: TideTrendLabel | null | undefined,
  observedAt: string,
  timeZone: string,
): FlaglerTideReading | null {
  if (!finite(feet) || !trend) return null;
  const timed = stamp(observedAt, timeZone);
  if (!timed) return null;
  const roundedFeet = round1(feet);
  return {
    feet: roundedFeet,
    meters: round1(roundedFeet / 3.28084),
    trend,
    stationId: FLAGLER_TIDE_STATION_ID,
    stationName: "Ponce de Leon Inlet",
    isPonceInlet: true,
    distanceKm: null,
    ...timed,
    nextHigh: null,
    nextLow: null,
  };
}

function publishedSpot(forecast: Pick<PublicForecastResponse, "spots">, breakId: string) {
  const ids = [
    publicSpotId(FLAGLER_REGION_ID, breakId),
    publicSpotId(FLAGLER_REGION_ID, FLAGLER_SURF_CAM_ID),
    publicSpotId(FLAGLER_REGION_ID, FLAGLER_PIER_ID),
  ];
  for (const id of ids) {
    const spot = forecast.spots.find((item) => item.id === id);
    if (!spot) continue;
    const fields = spot.forecast;
    if (
      finite(fields.swell_ft) ||
      finite(fields.tide_ft) ||
      finite(fields.water_temp_f) ||
      finite(fields.wind_mph)
    ) {
      return spot;
    }
  }
  return null;
}

/**
 * Flagler readings from the precomputed `/api/forecast.json` snapshot.
 * Swell, wind, tide, water temperature, and the Updated stamp all use
 * `generated_at`, the same instant the feed was published.
 */
export function readingsFromPublishedForecast(
  forecast: Pick<PublicForecastResponse, "generated_at" | "spots"> | null | undefined,
  breakId: string = FLAGLER_SURF_CAM_ID,
): FlaglerReadings | null {
  if (!forecast || Number.isNaN(Date.parse(forecast.generated_at))) return null;
  const spot = publishedSpot(forecast, breakId);
  if (!spot) return null;
  const timeZone = flaglerTimeZone();
  const surf = factsFromPublishedForecast({
    swellFt: spot.forecast.swell_ft,
    swellPeriodS: spot.forecast.swell_period_s,
    swellDirection: spot.forecast.swell_direction,
    windMph: spot.forecast.wind_mph,
    windDirection: spot.forecast.wind_direction,
    generatedAt: forecast.generated_at,
    timeZone,
  });
  const wind = surf
    ? windReading(surf.windMph, surf.windDir, forecast.generated_at, timeZone)
    : null;
  return {
    surf,
    surfObservedAt: surf ? forecast.generated_at : null,
    tide: tideFromPublished(
      spot.forecast.tide_ft,
      spot.forecast.tide_trend,
      forecast.generated_at,
      timeZone,
    ),
    water: waterFromFahrenheit(
      spot.forecast.water_temp_f,
      forecast.generated_at,
      timeZone,
    ),
    wind,
  };
}

/** Keep snapshot height and time. Fill the next high and low from the tide API. */
export function withTideExtremes(
  readings: FlaglerReadings,
  tide: TideData | null | undefined,
  timeZone: string,
  atIso: string = new Date().toISOString(),
): FlaglerReadings {
  if (!readings.tide) return readings;
  const live = tideReadingFromData(tide, atIso, timeZone);
  if (!live || live.stationId !== readings.tide.stationId) return readings;
  return {
    ...readings,
    tide: {
      ...readings.tide,
      nextHigh: live.nextHigh,
      nextLow: live.nextLow,
      distanceKm: readings.tide.distanceKm ?? live.distanceKm,
    },
  };
}

export function assembleFlaglerReadings(input: {
  surf: AnswerFacts | null;
  surfObservedAt: string | null;
  waterTempC: number | null;
  waterObservedAt: string | null;
  tide: TideData | null | undefined;
  wind: { mph: number; direction: string; observedAt: string } | null;
  nowIso: string;
  timeZone: string;
}): FlaglerReadings {
  const water = waterReading(input.waterTempC, input.waterObservedAt, input.timeZone);
  const wind = input.surf
    ? windReading(
        input.surf.windMph,
        input.surf.windDir,
        input.surfObservedAt ?? "",
        input.timeZone,
      )
    : input.wind
      ? windReading(
          input.wind.mph,
          input.wind.direction,
          input.wind.observedAt,
          input.timeZone,
        )
      : null;
  return {
    surf: input.surf,
    surfObservedAt: input.surf ? input.surfObservedAt : null,
    tide: tideReadingFromData(input.tide, input.nowIso, input.timeZone),
    water,
    wind,
  };
}

export function flaglerLastmod(readings: FlaglerReadings): string | null {
  const iso =
    readings.surfObservedAt ??
    readings.water?.timestampIso ??
    readings.wind?.timestampIso ??
    null;
  if (!iso || Number.isNaN(Date.parse(iso))) return null;
  return new Date(iso).toISOString();
}

function tideStationPhrase(tide: FlaglerTideReading): string {
  if (tide.isPonceInlet) return `${tide.stationName} (NOAA 8721147)`;
  return tide.stationName;
}

function tideParagraph(tide: FlaglerTideReading | null): string {
  if (!tide) {
    return "Flagler Beach tide is the prediction for NOAA station 8721147 at Ponce de Leon Inlet, the gauge used for this break. The height and trend show here when that prediction is available.";
  }
  const distance =
    tide.distanceKm != null
      ? ` The gauge is about ${Math.round(tide.distanceKm)} km from the break.`
      : "";
  const high = tide.nextHigh
    ? ` Next high is ${oneDecimal(tide.nextHigh.feet)} ft at ${tide.nextHigh.timestampLocal}.`
    : "";
  const low = tide.nextLow
    ? ` Next low is ${oneDecimal(tide.nextLow.feet)} ft at ${tide.nextLow.timestampLocal}.`
    : "";
  return `Flagler Beach tide is ${oneDecimal(tide.feet)} ft (${oneDecimal(tide.meters)} m) and ${tide.trend} at ${tideStationPhrase(tide)} as of ${tide.timestampLocal}.${distance}${high}${low}`;
}

function waterParagraph(water: FlaglerWaterReading | null): string {
  if (!water) {
    return "Flagler Beach water temperature is the sea-surface reading for this break, in Fahrenheit and Celsius, when the marine data includes it.";
  }
  return `Flagler Beach water temperature is ${water.fahrenheit}°F (${water.celsius}°C) as of ${water.timestampLocal}.`;
}

function windParagraph(wind: FlaglerWindReading | null): string {
  if (!wind)
    return "Wind speed and direction show here when the forecast reading includes them.";
  return `Wind at Flagler Beach is ${Math.round(wind.mph)} mph from the ${wind.direction} as of ${wind.timestampLocal}.`;
}

function swellParagraph(surf: AnswerFacts | null): string {
  if (!surf) {
    return "Swell height, period, and direction show here when the marine and wind readings are both in. Wave heights are in feet.";
  }
  const period = Math.round(surf.swellPeriodS);
  const face = surf.heightIsFace
    ? ` Surf face is about ${oneDecimal(surf.heightFt)} ft (${oneDecimal(surf.heightM)} m).`
    : "";
  return `Primary swell ${oneDecimal(surf.swellFt)} ft (${oneDecimal(surf.swellM)} m) at ${period}s from the ${surf.swellDir}.${face} Updated ${surf.timestampLocal}.`;
}

function camParagraph(): string {
  return "The FlaglerSurf cam is the town camera and opens on FlaglerSurf.com. The Flagler Beach Pier stream plays on howisthe.surf. Palm Coast does not have a cam on this site.";
}

const SECTION_ORDER: Record<FlaglerTopic, FlaglerSection["id"][]> = {
  report: ["surf", "tide", "water", "wind", "cam"],
  cam: ["cam", "surf", "tide", "water", "wind"],
  tide: ["tide", "surf", "water", "wind", "cam"],
  water: ["water", "surf", "tide", "wind", "cam"],
};

export function flaglerSections(
  page: FlaglerLanding,
  readings: FlaglerReadings,
): FlaglerSection[] {
  const camLinks: FlaglerLink[] = [];
  const camUrl = flaglerSurfCamUrl();
  if (camUrl) camLinks.push({ href: camUrl, label: "FlaglerSurf cam", external: true });
  const models: Record<string, FlaglerSection> = {
    surf: {
      id: "surf",
      heading: "Waves and swell",
      paragraphs: [swellParagraph(readings.surf)],
      links: [],
    },
    tide: {
      id: "tide",
      heading: "Tide at Ponce de Leon Inlet",
      paragraphs: [tideParagraph(readings.tide)],
      links: [],
    },
    water: {
      id: "water-temperature",
      heading: "Water temperature",
      paragraphs: [waterParagraph(readings.water)],
      links: [],
    },
    wind: {
      id: "wind",
      heading: "Wind",
      paragraphs: [windParagraph(readings.wind)],
      links: [],
    },
    cam: {
      id: "surf-cam",
      heading: "FlaglerSurf cam and pier stream",
      paragraphs: [camParagraph()],
      links: camLinks,
    },
  };
  return SECTION_ORDER[page.topic].map((id) => models[id]);
}

export function flaglerInternalLinks(pathname: string): FlaglerLink[] {
  const links: FlaglerLink[] = [
    ...flaglerLandingPages.map((page) => ({ href: page.path, label: page.linkLabel })),
    { href: "/", label: "Home" },
    { href: "/northeast-florida-surf-report", label: "Northeast Florida surf report" },
    { href: "/florida-surf-reports", label: "Florida surf reports" },
    { href: "/st-augustine-surf-report", label: "St Augustine surf report" },
    { href: "/jax-beach-surf-report", label: "Jax Beach surf report" },
    { href: "/ormond-beach-surf-report", label: "Ormond Beach surf report" },
  ];
  return links.filter((link) => link.href !== pathname);
}

export function flaglerLocationLine(page: FlaglerLanding): string {
  return `${page.placeName} is located in Flagler Beach, FL (${page.lat.toFixed(4)}, ${page.lon.toFixed(4)}).`;
}

function clip(text: string, max = 160): string {
  if (text.length <= max) return text;
  const sliced = text.slice(0, max - 1);
  const word = sliced.replace(/\s+\S*$/, "");
  return `${word.length > 40 ? word : sliced}…`;
}

/** Keep the Updated stamp when the description is longer than the meta limit. */
function clipKeepingStamp(text: string, stamp: string | null): string {
  if (!stamp) return clip(text);
  const suffix = `Updated ${stamp}.`;
  if (!text.endsWith(suffix) || text.length <= 160) return clip(text);
  const room = 160 - suffix.length - 1;
  if (room < 24) return clip(text);
  return `${clip(text.slice(0, text.length - suffix.length).trimEnd(), room)} ${suffix}`;
}

function shortTide(tide: FlaglerTideReading): string {
  return `Flagler Beach tide is ${oneDecimal(tide.feet)} ft and ${tide.trend} at ${tideStationPhrase(tide)}.`;
}

function shortWater(water: FlaglerWaterReading): string {
  return `Flagler Beach water temperature is ${water.fahrenheit}°F (${water.celsius}°C).`;
}

function shortSurf(surf: AnswerFacts): string {
  const period = Math.round(surf.swellPeriodS);
  return `Flagler Beach surf report: swell ${oneDecimal(surf.swellFt)} ft at ${period}s ${surf.swellDir}.`;
}

export function flaglerMetaDescription(
  page: FlaglerLanding,
  readings: FlaglerReadings,
): string {
  const bits: string[] = [];
  if (page.topic === "tide") {
    if (!readings.tide) return page.description;
    bits.push(shortTide(readings.tide));
    if (readings.water) bits.push(shortWater(readings.water));
  } else if (page.topic === "water") {
    if (!readings.water) return page.description;
    bits.push(shortWater(readings.water));
    if (readings.tide) bits.push(shortTide(readings.tide));
  } else if (page.topic === "cam") {
    bits.push("Flagler Beach surf cam: FlaglerSurf camera and the pier stream.");
    if (readings.surf) bits.push(shortSurf(readings.surf));
  } else if (readings.surf || readings.tide || readings.water) {
    if (readings.surf) bits.push(shortSurf(readings.surf));
    if (readings.tide) bits.push(shortTide(readings.tide));
    if (readings.water) bits.push(shortWater(readings.water));
  }
  if (bits.length === 0) return page.description;
  const time =
    (page.topic === "tide" ? readings.tide?.timestampLocal : null) ??
    (page.topic === "water" ? readings.water?.timestampLocal : null) ??
    readings.surf?.timestampLocal ??
    readings.water?.timestampLocal ??
    readings.tide?.timestampLocal ??
    null;
  const text = time ? `${bits.join(" ")} Updated ${time}.` : bits.join(" ");
  const withPhrase = text.toLowerCase().includes(page.h1.toLowerCase())
    ? text
    : `${page.h1}. ${text}`;
  return clipKeepingStamp(withPhrase, time);
}

function tideFaq(tide: FlaglerTideReading | null): string {
  if (!tide) {
    return "The Flagler Beach tide page shows the current height and trend from NOAA station 8721147 at Ponce de Leon Inlet when that prediction is available.";
  }
  return `As of ${tide.timestampLocal}, Flagler Beach tide is ${oneDecimal(tide.feet)} ft (${oneDecimal(tide.meters)} m) and ${tide.trend} at ${tideStationPhrase(tide)}.`;
}

function waterFaq(water: FlaglerWaterReading | null): string {
  if (!water) {
    return "Flagler Beach water temperature is the sea-surface reading for this break. The page shows Fahrenheit and Celsius when the marine data includes it, and leaves the number out when it does not.";
  }
  return `As of ${water.timestampLocal}, Flagler Beach water temperature is ${water.fahrenheit}°F (${water.celsius}°C).`;
}

export function flaglerReadingFaqs(readings: FlaglerReadings): FaqEntry[] {
  return [
    {
      question: "What is the Flagler Beach tide right now?",
      answer: tideFaq(readings.tide),
    },
    {
      question: "What is the Flagler Beach water temperature?",
      answer: waterFaq(readings.water),
    },
    {
      question: "Does Palm Coast have its own surf report here?",
      answer: PALM_COAST_ANSWER,
    },
  ];
}

function topicFaqs(topic: FlaglerTopic): FaqEntry[] {
  if (topic === "cam") {
    return [
      {
        question: "Where is the Flagler Beach surf cam?",
        answer:
          "The FlaglerSurf cam is the town camera, linked from this page. The Flagler Beach Pier stream plays on howisthe.surf. Both sit with the Flagler Beach swell, tide, and water temperature.",
      },
      {
        question: "Is the FlaglerSurf cam embedded on howisthe.surf?",
        answer:
          "No. The FlaglerSurf camera opens on FlaglerSurf.com in a new tab. The pier camera is the live stream embedded on howisthe.surf.",
      },
    ];
  }
  if (topic === "tide") {
    return [
      {
        question: "Where does the Flagler Beach tide come from?",
        answer:
          "Flagler Beach tide on howisthe.surf is the harmonic prediction for NOAA station 8721147, Ponce de Leon Inlet, on mean lower low water (MLLW). That gauge is the one selected for Flagler Pier and the FlaglerSurf cam. A height is shown only when the prediction is available.",
      },
      {
        question: "Is the Flagler Beach tide the same gauge as Ponce Inlet?",
        answer:
          "Yes. Flagler Beach and Ponce Inlet use NOAA station 8721147 on this site. The inlet is south of Flagler, so treat it as the local reference rather than a gauge on the pier.",
      },
    ];
  }
  if (topic === "water") {
    return [
      {
        question: "How is Flagler Beach water temperature measured?",
        answer:
          "The number is the sea-surface temperature from the marine forecast for the Flagler Beach break. If that field is missing, the page does not fill one in.",
      },
      {
        question: "Is the Flagler Beach water temperature in Fahrenheit?",
        answer:
          "Yes. Flagler Beach water temperature is shown in Fahrenheit first, with Celsius in parentheses.",
      },
    ];
  }
  return [];
}

export function flaglerPageFaqs(
  page: FlaglerLanding,
  readings: FlaglerReadings,
  base: FaqEntry[] | null,
): FaqEntry[] {
  if (page.topic === "report") return [...(base ?? []), ...flaglerReadingFaqs(readings)];
  return [...topicFaqs(page.topic), ...flaglerReadingFaqs(readings)];
}

export function flaglerReportSentence(readings: FlaglerReadings): string | null {
  const config = answerConfigForPath(FLAGLER_REPORT_PATH);
  if (!config || !readings.surf) return null;
  return formatConditionsSentence(config, readings.surf);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function renderFlaglerArticle(input: {
  page: FlaglerLanding;
  readings: FlaglerReadings;
  faqs: FaqEntry[];
}): string {
  const parts: string[] = [`<h1>${escapeHtml(input.page.h1)}</h1>`];
  const sentence =
    input.page.topic === "report" ? flaglerReportSentence(input.readings) : null;
  if (sentence) parts.push(`<p>${escapeHtml(sentence)}</p>`);
  parts.push(`<p>${escapeHtml(input.page.intro)}</p>`);
  parts.push(`<p>${escapeHtml(flaglerLocationLine(input.page))}</p>`);
  for (const section of flaglerSections(input.page, input.readings)) {
    parts.push(`<h2 id="${escapeHtml(section.id)}">${escapeHtml(section.heading)}</h2>`);
    for (const paragraph of section.paragraphs) {
      parts.push(`<p>${escapeHtml(paragraph)}</p>`);
    }
    if (section.links.length > 0) {
      parts.push(
        `<ul>${section.links
          .map((link) => {
            const rel = link.external ? ` target="_blank" rel="noopener noreferrer"` : "";
            return `<li><a href="${escapeHtml(link.href)}"${rel}>${escapeHtml(link.label)}</a></li>`;
          })
          .join("")}</ul>`,
      );
    }
  }
  if (input.page.topic === "report") {
    parts.push("<h2>About this report</h2>");
    parts.push(`<p>${escapeHtml(aboutReport("us", input.page.placeName))}</p>`);
  }
  const related = flaglerInternalLinks(input.page.path);
  if (related.length > 0) {
    parts.push("<h2>Related pages</h2>");
    parts.push(
      `<ul>${related
        .map(
          (link) =>
            `<li><a href="${escapeHtml(link.href)}">${escapeHtml(link.label)}</a></li>`,
        )
        .join("")}</ul>`,
    );
  }
  if (input.faqs.length > 0) {
    parts.push("<h2>Common questions</h2>");
    for (const faq of input.faqs) {
      parts.push(`<h3>${escapeHtml(faq.question)}</h3>`);
      parts.push(`<p>${escapeHtml(faq.answer)}</p>`);
    }
  }
  return `<article id="seo-content">${parts.join("")}</article>`;
}

export const FLAGLER_SNAPSHOT_SCRIPT_ID = "flagler-forecast-snapshot";

/** Flagler spots only, so the landing HTML does not embed the whole catalog. */
export function flaglerForecastEmbed(
  forecast: Pick<PublicForecastResponse, "generated_at" | "spots"> | null | undefined,
): string {
  if (!forecast) return "";
  const ids = new Set([
    publicSpotId(FLAGLER_REGION_ID, FLAGLER_SURF_CAM_ID),
    publicSpotId(FLAGLER_REGION_ID, FLAGLER_PIER_ID),
  ]);
  const spots = forecast.spots.filter((spot) => ids.has(spot.id));
  if (spots.length === 0) return "";
  const payload = JSON.stringify({
    api_version: 1,
    generated_at: forecast.generated_at,
    spots,
  }).replace(/</g, "\\u003c");
  return `<script id="${FLAGLER_SNAPSHOT_SCRIPT_ID}" type="application/json">${payload}</script>`;
}

function replaceHtmlTag(html: string, pattern: RegExp, replacement: string): string {
  if (!pattern.test(html)) {
    throw new Error(`Unable to find tag while rendering Flagler page: ${pattern}`);
  }
  return html.replace(pattern, replacement);
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * Splice a Flagler landing into the Vite shell. The article and the
 * embedded snapshot use the same published forecast, so the first HTML
 * response and the first React paint share `generated_at`.
 */
export function renderFlaglerLandingDocument(input: {
  shellHtml: string;
  page: FlaglerLanding;
  readings: FlaglerReadings;
  faqs: FaqEntry[];
  forecast?: Pick<PublicForecastResponse, "generated_at" | "spots"> | null;
}): string {
  const description = flaglerMetaDescription(input.page, input.readings);
  const title = escapeAttr(input.page.title);
  const desc = escapeAttr(description);
  const url = escapeAttr(getCanonicalUrl(input.page.path));
  let html = input.shellHtml.replace(
    /\s*<script type="application\/ld\+json" id="website-structured-data">[\s\S]*?<\/script>/,
    "",
  );
  html = replaceHtmlTag(html, /<title>.*?<\/title>/s, `<title>${title}</title>`);
  html = replaceHtmlTag(
    html,
    /<meta name="description" content="[^"]*" \/>/,
    `<meta name="description" content="${desc}" />`,
  );
  html = replaceHtmlTag(
    html,
    /<link rel="canonical" href="[^"]*" \/>/,
    `<link rel="canonical" href="${url}" />`,
  );
  html = replaceHtmlTag(
    html,
    /<meta property="og:title" content="[^"]*" \/>/,
    `<meta property="og:title" content="${title}" />`,
  );
  html = replaceHtmlTag(
    html,
    /<meta property="og:description" content="[^"]*" \/>/,
    `<meta property="og:description" content="${desc}" />`,
  );
  html = replaceHtmlTag(
    html,
    /<meta property="og:url" content="[^"]*" \/>/,
    `<meta property="og:url" content="${url}" />`,
  );
  html = replaceHtmlTag(
    html,
    /<meta name="twitter:title" content="[^"]*" \/>/,
    `<meta name="twitter:title" content="${title}" />`,
  );
  html = replaceHtmlTag(
    html,
    /<meta name="twitter:description" content="[^"]*" \/>/,
    `<meta name="twitter:description" content="${desc}" />`,
  );
  const scripts = [
    `<script type="application/ld+json" id="page-structured-data">${JSON.stringify(
      buildFlaglerJsonLd(input.page, input.readings),
    ).replace(/</g, "\\u003c")}</script>`,
  ];
  if (input.faqs.length > 0) {
    scripts.push(
      `<script type="application/ld+json" id="faq-structured-data">${JSON.stringify(
        buildFaqJsonLd(input.faqs),
      ).replace(/</g, "\\u003c")}</script>`,
    );
  }
  html = html.replace("</head>", `    ${scripts.join("\n    ")}\n  </head>`);
  const root = /<div id="root">[\s\S]*?<\/div>/;
  if (!root.test(html)) throw new Error("Unable to find #root in the Flagler shell");
  html = html.replace(
    root,
    `<div id="root">${renderFlaglerArticle({
      page: input.page,
      readings: input.readings,
      faqs: input.faqs,
    })}</div>`,
  );
  const embed = flaglerForecastEmbed(input.forecast);
  if (embed) html = html.replace("</body>", `    ${embed}\n  </body>`);
  return html;
}

export function buildFlaglerJsonLd(
  page: FlaglerLanding,
  readings: FlaglerReadings,
): Record<string, unknown> {
  const url = getCanonicalUrl(page.path);
  const beachId = `${url}#beach`;
  const description = flaglerMetaDescription(page, readings);
  const variables: Record<string, unknown>[] = [];
  if (readings.surf) {
    variables.push({
      "@type": "PropertyValue",
      name: "Swell height",
      value: readings.surf.swellFt,
      unitText: "ft",
    });
    if (readings.wind) {
      variables.push({
        "@type": "PropertyValue",
        name: "Wind speed",
        value: Math.round(readings.wind.mph),
        unitText: "mph",
      });
    }
  }
  if (readings.tide) {
    variables.push({
      "@type": "PropertyValue",
      name: "Tide height",
      value: readings.tide.feet,
      unitText: "ft",
      measurementTechnique: readings.tide.isPonceInlet
        ? "NOAA harmonic prediction, station 8721147, MLLW"
        : readings.tide.stationName,
    });
  }
  if (readings.water) {
    variables.push({
      "@type": "PropertyValue",
      name: "Water temperature",
      value: readings.water.fahrenheit,
      unitText: "F",
    });
  }
  const graph: Record<string, unknown>[] = [
    {
      "@type": "WebPage",
      "@id": `${url}#webpage`,
      url,
      name: page.title,
      description,
      inLanguage: "en",
      isPartOf: {
        "@type": "WebSite",
        name: "howisthe.surf",
        url: getCanonicalUrl("/"),
      },
      about: { "@id": beachId },
      significantLink: flaglerSurfCamUrl() || undefined,
    },
    {
      "@type": "Beach",
      "@id": beachId,
      name: "Flagler Beach",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Flagler Beach",
        addressRegion: "FL",
        addressCountry: "US",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: page.lat,
        longitude: page.lon,
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: getCanonicalUrl("/") },
        {
          "@type": "ListItem",
          position: 2,
          name: "Northeast Florida",
          item: getCanonicalUrl("/northeast-florida-surf-report"),
        },
        { "@type": "ListItem", position: 3, name: page.h1, item: url },
      ],
    },
  ];
  const observed = flaglerLastmod(readings);
  if (variables.length > 0 && observed) {
    graph.push({
      "@type": "Dataset",
      name: "Flagler Beach conditions",
      description:
        "Surf, tide, wind, and water temperature values published on this page. A value is included only when a reading was available.",
      url,
      temporalCoverage: observed,
      spatialCoverage: { "@id": beachId },
      variableMeasured: variables,
    });
  }
  return { "@context": "https://schema.org", "@graph": graph };
}

export function labelForFlaglerPath(pathname: string): string | null {
  return getFlaglerLanding(pathname)?.linkLabel ?? null;
}
